import { useEffect, useMemo, useRef, useState } from 'react'
import type { ChangeEvent, DragEvent } from 'react'
import './App.css'
import { config } from './config'
import { createClient, createConfig, type Client } from './generated/client'
import { health, presignUpload, completeUpload, createJob, createRun, getLatestSuccessfulRun } from './generated'
import { uploadFileToPresignedUrl } from './api/upload'

const MAX_FILE_SIZE = 10 * 1024 * 1024
const POLL_INTERVAL_SECONDS = 5

type WorkflowPhase = 'idle' | 'uploading' | 'polling' | 'ready' | 'failed'

function formatBytes(bytes: number) {
  if (bytes < 1024) {
    return `${bytes} B`
  }

  const units = ['KB', 'MB', 'GB']
  let size = bytes / 1024
  let unitIndex = 0

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex += 1
  }

  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unitIndex]}`
}
export async function testHost(client: Client) {
  const h = await health({ client })
  return h
}

function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

const client = createClient(
  createConfig({
    baseUrl: config.apiUrl,
  })
)

function App() {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const isMountedRef = useRef(true)
  const shouldPollRef = useRef(false)
  const [dragActive, setDragActive] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [phase, setPhase] = useState<WorkflowPhase>('idle')
  const [activeJobId, setActiveJobId] = useState<number | null>(null)
  const [activeRunId, setActiveRunId] = useState<string>('')
  const [downloadUrl, setDownloadUrl] = useState('')
  const [statusMessage, setStatusMessage] = useState('')
  const [nextPollInSeconds, setNextPollInSeconds] = useState(POLL_INTERVAL_SECONDS)

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl('')
      return undefined
    }

    const objectUrl = URL.createObjectURL(selectedFile)
    setPreviewUrl(objectUrl)

    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [selectedFile])

  const fileDetails = useMemo(() => {
    if (!selectedFile) {
      return []
    }

    return [
      { label: 'Name', value: selectedFile.name },
      { label: 'Type', value: selectedFile.type || 'Unknown' },
      { label: 'Size', value: formatBytes(selectedFile.size) },
    ]
  }, [selectedFile])

  async function applyFile(file: File | undefined) {
    if (!file) {
      return
    }

    if (!file.type.startsWith('image/')) {
      setErrorMessage('Select a valid image file.')
      setSelectedFile(null)
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      setErrorMessage('Choose an image smaller than 10 MB.')
      setSelectedFile(null)
      return
    }

    setErrorMessage('')
    setSelectedFile(file)
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    applyFile(event.target.files?.[0])
  }

  function handleDragOver(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault()
    setDragActive(true)
  }

  function handleDragLeave(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault()
    setDragActive(false)
  }

  function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault()
    setDragActive(false)
    applyFile(event.dataTransfer.files?.[0])
  }

  function clearImage() {
    setSelectedFile(null)
    setErrorMessage('')

    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  function resetWorkflow() {
    shouldPollRef.current = false
    clearImage()
    setPhase('idle')
    setActiveJobId(null)
    setActiveRunId('')
    setDownloadUrl('')
    setStatusMessage('')
    setNextPollInSeconds(POLL_INTERVAL_SECONDS)
  }

  async function pollForCompletedRun(jobId: number) {
    shouldPollRef.current = true
    setPhase('polling')
    setStatusMessage('Processing image. This page checks for updates every 5 seconds.')
    setNextPollInSeconds(POLL_INTERVAL_SECONDS)

    while (isMountedRef.current && shouldPollRef.current) {
      const latestRunResp = await getLatestSuccessfulRun({
        client,
        path: { jobId },
      })

      const currentStatus = latestRunResp.data?.status ?? 'PENDING'
      const currentDownloadUrl = latestRunResp.data?.uploadUrl ?? ''

      if (currentStatus.toUpperCase() === 'DONE') {
        if (!currentDownloadUrl) {
          throw new Error('Run completed, but no download URL was returned.')
        }

        shouldPollRef.current = false
        setDownloadUrl(currentDownloadUrl)
        setStatusMessage('Your result is ready.')
        setPhase('ready')
        return
      }

      setStatusMessage(`Current status: ${currentStatus}`)

      for (let remaining = POLL_INTERVAL_SECONDS; remaining > 0; remaining -= 1) {
        if (!shouldPollRef.current) {
          return
        }

        setNextPollInSeconds(remaining)
        await sleep(1000)
      }
    }
  }

  async function handleSubmit() {
    if (!selectedFile) {
      setErrorMessage('No file selected to upload.')
      return
    }

    setErrorMessage('')
    shouldPollRef.current = true
    setPhase('uploading')
    setStatusMessage('Uploading image and creating your processing job...')

    try {
      const presignResp = await presignUpload({
        client,
        body: {
          filename: selectedFile.name,
          contentType: selectedFile.type,
          sizeBytes: selectedFile.size,
        },
      })

      const objectKey = presignResp.data?.objectKey ?? ''
      const uploadUrl = presignResp.data?.uploadUrl
      const fileUUID = presignResp.data?.fileUUID ?? ''

      if (!uploadUrl) {
        throw new Error('Upload URL was not returned by the API.')
      }

      await uploadFileToPresignedUrl(selectedFile, uploadUrl)
      await completeUpload({ client, body: { objectKey } })

      const jobResp = await createJob({ client, body: { fileUUID } })
      const jobId = jobResp.data?.jobId

      if (!jobId) {
        throw new Error('Job was created without a valid jobId.')
      }

      const runResp = await createRun({ client, body: { jobId } })
      const runId = runResp.data?.runId

      if (!runId) {
        throw new Error('Run was created without a valid runId.')
      }

      setActiveJobId(jobId)
      setActiveRunId(runId)
      await pollForCompletedRun(jobId)
    } catch (error) {
      shouldPollRef.current = false
      setPhase('failed')
      setStatusMessage('')
      setErrorMessage(error instanceof Error ? error.message : 'Upload failed unexpectedly.')
    }
  }

  return (
    <main className="app-shell">
      <section className="hero-panel">
        <p className="eyebrow">Client-side image upload</p>
        <h1>Choose an image, preview it, and prepare it for upload.</h1>
        <p className="hero-copy">
          This uploader accepts image files only, supports drag and drop, and lets you
          review image metadata before wiring in the real upload API call.
        </p>
      </section>

      <section className="uploader-grid" aria-label="Image upload workspace">
        {phase === 'idle' ? (
          <>
            <label
              className={`dropzone ${dragActive ? 'dropzone-active' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={inputRef}
                className="file-input"
                type="file"
                accept="image/*"
                onChange={handleInputChange}
              />
              <span className="dropzone-icon" aria-hidden="true">
                ↑
              </span>
              <span className="dropzone-title">Drop an image here</span>
              <span className="dropzone-subtitle">
                or click to browse your device for JPG, PNG, GIF, WebP, and more
              </span>
              <span className="dropzone-footnote">Maximum file size: 10 MB</span>
            </label>

            <div className="preview-card">
              <div className="preview-frame">
                {previewUrl ? (
                  <img src={previewUrl} alt={selectedFile?.name || 'Selected preview'} />
                ) : (
                  <div className="preview-placeholder">
                    <span>No image selected</span>
                    <p>Your preview will appear here as soon as you choose a file.</p>
                  </div>
                )}
              </div>

              <div className="details-panel">
                <div>
                  <h2>File details</h2>
                  <p>
                    {selectedFile
                      ? 'Review this image, then click Upload image to trigger processing and polling.'
                      : 'Pick an image to prepare it for upload and populate its metadata.'}
                  </p>
                </div>

                {errorMessage ? <p className="error-message">{errorMessage}</p> : null}

                {fileDetails.length > 0 ? (
                  <dl className="file-details">
                    {fileDetails.map((item) => (
                      <div key={item.label}>
                        <dt>{item.label}</dt>
                        <dd>{item.value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}

                <div className="action-row">
                  <button type="button" className="primary-button" onClick={() => inputRef.current?.click()}>
                    Choose image
                  </button>
                  <button type="button" className="primary-button" onClick={handleSubmit} disabled={!selectedFile}>
                    Upload image
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={clearImage}
                    disabled={!selectedFile && !errorMessage}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="preview-card preview-card-full">
            <div className="preview-frame">
              {previewUrl ? (
                <img src={previewUrl} alt={selectedFile?.name || 'Uploaded preview'} />
              ) : (
                <div className="preview-placeholder">
                  <span>Processing</span>
                  <p>We are preparing your result.</p>
                </div>
              )}
            </div>

            <div className="details-panel status-panel">
              <div>
                <h2>
                  {phase === 'uploading' && 'Uploading image'}
                  {phase === 'polling' && 'Waiting for result'}
                  {phase === 'ready' && 'Download ready'}
                  {phase === 'failed' && 'Something went wrong'}
                </h2>
                <p>{statusMessage}</p>
              </div>

              {(phase === 'uploading' || phase === 'polling') && (
                <div className="poll-pill" role="status" aria-live="polite">
                  {phase === 'uploading'
                    ? 'Finishing upload setup...'
                    : `Next backend check in ${nextPollInSeconds}s`}
                </div>
              )}

              {activeJobId ? (
                <p className="job-id">Job ID: {activeJobId}</p>
              ) : null}

              {activeRunId ? (
                <p className="job-id">Run ID: {activeRunId}</p>
              ) : null}

              {errorMessage ? <p className="error-message">{errorMessage}</p> : null}

              <div className="action-row">
                {phase === 'ready' && downloadUrl ? (
                  <a className="primary-button action-link" href={downloadUrl} target="_blank" rel="noreferrer">
                    Download result
                  </a>
                ) : null}
                {phase === 'failed' ? (
                  <button type="button" className="primary-button" onClick={handleSubmit} disabled={!selectedFile}>
                    Retry
                  </button>
                ) : null}
                <button type="button" className="secondary-button" onClick={resetWorkflow}>
                  {phase === 'ready' ? 'Upload another image' : 'Back to upload'}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  )
}

export default App
